import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { BackgroundSaveService } from '../services/backgroundSaveService';
import { useAlert } from '../contexts/AlertContext';
import { useSimpleCaseContext } from '../contexts/SimpleCaseContext';
import { debugLog, debugError } from '../utils/debug';
import { config } from '../config/environment';

interface NavigationGuardOptions {
    isWizardPage: boolean;
    hasUnsavedChanges?: boolean;
    forceSave?: () => Promise<void>;
    autoSaveEnabled?: boolean;
    /**
     * Manual Save mode (ManualSaveToggle flag AND the admin's ManualSaveEnabled setting).
     * When true, nothing here saves automatically — every path either prompts the user
     * (in-app navigation, back button) or defers to the browser's native "unsaved changes"
     * dialog (refresh/close), per docs/AUTOSAVE_DETAILS.md "Manual Save mode".
     */
    manualMode?: boolean;
    /**
     * ConcurrentCaseEditGuard: clears the "currently editing" marker (SimpleCaseContext's
     * releaseCaseEditingLock). Awaited right before every navigate()/action() that leaves the
     * wizard for a different page (Cases/Settings/Admin buttons, logout, back button) --
     * NOT on refresh/reload paths (performSaveAndReload, handleBeforeUnload, handlePageHide),
     * which stay on the same case. Guards against a real race: the marker-clear request and
     * the destination page's own data fetch (e.g. HomePage's case list) have no ordering
     * guarantee if the clear is only fired fire-and-forget from an unmount cleanup -- fine on
     * a fast local network, but reproducible with real latency (see docs/AUTOSAVE_DETAILS.md).
     */
    releaseCaseEditingLock?: () => Promise<void>;
}

export const useNavigationGuard = ({ isWizardPage, hasUnsavedChanges = false, forceSave, autoSaveEnabled = false, manualMode = false, releaseCaseEditingLock }: NavigationGuardOptions) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { addAlert } = useAlert();
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    // Set by checkAction for a non-navigation exit (e.g. logout) that also needs the
    // unsaved-changes gate. Mutually exclusive with pendingNavigation — handleSave/handleDiscard
    // run this instead of navigate() when present.
    const [pendingAction, setPendingAction] = useState<(() => void | Promise<void>) | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [hasShownModalForBeforeUnload, setHasShownModalForBeforeUnload] = useState(false);
    const isHandlingBackButtonRef = useRef(false);
    const previousLocationRef = useRef(location.pathname);
    const previousPathBeforeBackRef = useRef<string | null>(null);
    const isHandlingRefreshRef = useRef(false);

    // Try to get case data from context as fallback
    let currentCase: { case?: any; etag?: string } | null = null;
    let formData: any = null;
    try {
        const context = useSimpleCaseContext();
        currentCase = context.currentCase;
        formData = context.formData;
    } catch (error) {
        // Context not available, will use forceSave if provided
    }

    // ConcurrentCaseEditGuard: release the editing-marker lock before actually leaving the
    // wizard. No-op when releaseCaseEditingLock is absent/the guard isn't active, so it's safe
    // to call unconditionally from every exit path below.
    const releaseLockThenNavigate = useCallback(async (targetPath: string | number) => {
        if (releaseCaseEditingLock) {
            try {
                await releaseCaseEditingLock();
            } catch (err) {
                debugError('useNavigationGuard: releaseCaseEditingLock failed', err);
            }
        }
        if (typeof targetPath === 'string') {
            navigate(targetPath);
        } else {
            navigate(targetPath);
        }
    }, [navigate, releaseCaseEditingLock]);

    const releaseLockThenAction = useCallback(async (action: () => void | Promise<void>) => {
        if (releaseCaseEditingLock) {
            try {
                await releaseCaseEditingLock();
            } catch (err) {
                debugError('useNavigationGuard: releaseCaseEditingLock failed', err);
            }
        }
        await action();
    }, [releaseCaseEditingLock]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            if (forceSave) {
                await forceSave();
            } else if (currentCase?.case?.basic?.caseId && (formData || currentCase.case)) {
                // Fallback: drain the BackgroundSaveService directly. The service reads the
                // latest case body from the registered provider so explicit args aren't needed.
                await BackgroundSaveService.flush();
            } else {
                debugError('Cannot save: forceSave function not provided and no case data available');
                alert('Cannot save: Save function not available. Please try again.');
                return;
            }
            setShowSaveModal(false);
            setHasShownModalForBeforeUnload(false);
            if (pendingAction) {
                const action = pendingAction;
                setPendingAction(null);
                await releaseLockThenAction(action);
            } else if (pendingNavigation) {
                const target = pendingNavigation;
                setPendingNavigation(null);
                await releaseLockThenNavigate(target);
            }
            // If neither (browser refresh/close), just close the modal
            // The user can then proceed with their browser action
        } catch (error) {
            debugError('Save failed:', error);
            alert('Save failed. Please try again or your changes will be lost.');
        } finally {
            setIsSaving(false);
        }
    }, [pendingNavigation, pendingAction, forceSave, currentCase, formData, releaseLockThenNavigate, releaseLockThenAction]);

    const handleDiscard = useCallback(async () => {
        setShowSaveModal(false);
        setHasShownModalForBeforeUnload(false);
        if (pendingAction) {
            const action = pendingAction;
            setPendingAction(null);
            await releaseLockThenAction(action);
        } else if (pendingNavigation) {
            const target = pendingNavigation;
            setPendingNavigation(null);
            await releaseLockThenNavigate(target);
        }
        // If neither (browser refresh/close), just close the modal
        // The user can then proceed with their browser action
    }, [pendingNavigation, pendingAction, releaseLockThenNavigate, releaseLockThenAction]);

    const handleCancel = useCallback(() => {
        setShowSaveModal(false);
        setPendingNavigation(null);
        setPendingAction(null);
        setHasShownModalForBeforeUnload(false);
    }, []);

    const checkNavigation = useCallback(async (targetPath: string | number) => {
        if (!isWizardPage) {
            await releaseLockThenNavigate(targetPath);
            return;
        }

        // Manual Save mode: never save silently. Prompt the user to choose Save/Discard/Cancel
        // via the same SaveConfirmationModal used as the autosave-mode fallback.
        if (manualMode && hasUnsavedChanges) {
            setPendingNavigation(typeof targetPath === 'string' ? targetPath : null);
            setShowSaveModal(true);
            return;
        }

        // Always save before navigation if there are unsaved changes
        if (hasUnsavedChanges && forceSave) {
            setIsSaving(true);
            try {
                await forceSave();
                // Navigate after successful save
                await releaseLockThenNavigate(targetPath);
            } catch (error) {
                debugError('Save before navigation failed:', error);
                // If save fails, show the modal as fallback
                setPendingNavigation(typeof targetPath === 'string' ? targetPath : null);
                setShowSaveModal(true);
            } finally {
                setIsSaving(false);
            }
            return;
        }

        // No unsaved changes, navigate immediately
        await releaseLockThenNavigate(targetPath);
    }, [isWizardPage, hasUnsavedChanges, manualMode, forceSave, releaseLockThenNavigate]);

    /**
     * Same unsaved-changes gate as checkNavigation, but for an exit action that isn't a
     * react-router navigation — e.g. logout, which clears auth state directly and lets
     * ProtectedRoute swap SimpleCaseProvider out for a redirect once isAuthenticated flips
     * false (unmounting it exactly like leaving the wizard would). Runs `action` immediately
     * if there's nothing unsaved; otherwise saves-then-runs, or in Manual Save mode shows the
     * same SaveConfirmationModal (Save/Discard/Cancel) as checkNavigation.
     */
    const checkAction = useCallback(async (action: () => void | Promise<void>) => {
        if (!isWizardPage) {
            await releaseLockThenAction(action);
            return;
        }

        if (manualMode && hasUnsavedChanges) {
            setPendingAction(() => action);
            setShowSaveModal(true);
            return;
        }

        if (hasUnsavedChanges && forceSave) {
            setIsSaving(true);
            try {
                await forceSave();
                await releaseLockThenAction(action);
            } catch (error) {
                debugError('Save before action failed:', error);
                setPendingAction(() => action);
                setShowSaveModal(true);
            } finally {
                setIsSaving(false);
            }
            return;
        }

        await releaseLockThenAction(action);
    }, [isWizardPage, hasUnsavedChanges, manualMode, forceSave, releaseLockThenAction]);

    // Helper function to perform save and reload
    const performSaveAndReload = useCallback(async () => {
        isHandlingRefreshRef.current = true;
        try {
            if (currentCase?.case?.basic?.caseId && (formData || currentCase.case)) {
                const caseData = formData || currentCase.case;
                const caseId = currentCase.case.basic.caseId;
                const etag = currentCase.etag || '';

                // Normalize stepDowns if needed (same logic as caseApiService)
                const normalizedCaseData = { ...caseData };
                if (normalizedCaseData.parties?.partyA?.tax?.stepDowns) {
                    const stepDownsA = normalizedCaseData.parties.partyA.tax.stepDowns;
                    if (!Array.isArray(stepDownsA) && typeof stepDownsA === 'object') {
                        const keys = Object.keys(stepDownsA).map(k => parseInt(k)).filter(k => !isNaN(k)).sort((a, b) => a - b);
                        normalizedCaseData.parties.partyA.tax.stepDowns = keys.map(k => (stepDownsA as any)[k.toString()]);
                    }
                }
                if (normalizedCaseData.parties?.partyB?.tax?.stepDowns) {
                    const stepDownsB = normalizedCaseData.parties.partyB.tax.stepDowns;
                    if (!Array.isArray(stepDownsB) && typeof stepDownsB === 'object') {
                        const keys = Object.keys(stepDownsB).map(k => parseInt(k)).filter(k => !isNaN(k)).sort((a, b) => a - b);
                        normalizedCaseData.parties.partyB.tax.stepDowns = keys.map(k => (stepDownsB as any)[k.toString()]);
                    }
                }

                // Make save request and wait for it to complete
                // Use the no-merge endpoint to preserve the master-copy invariant — server must
                // not auto-integrate client-submitted expenses on a tab-close path.
                const url = `${config.apiBaseUrl}/v1/cases/save-no-merge`;
                // If-Match makes this a conditional write. Without it the server treats the save
                // as an unconditional overwrite, so a tab holding a stale snapshot destroys newer
                // work saved from another tab or device. A 412 here is the correct outcome, not a
                // failure to recover from: this copy is the older one.
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(etag ? { 'If-Match': etag } : {}),
                    },
                    body: JSON.stringify(normalizedCaseData),
                    credentials: 'include',
                });

                if (response.status === 412) {
                    // Someone else's newer version is on the server, and the write was correctly
                    // refused — overwriting would have lost their work to preserve our staler
                    // copy. Still reload: this is "save and reload", and the reload is what
                    // replaces this tab's stale snapshot with the newer version.
                    debugError('Unload save refused: the case was modified elsewhere (412). Reloading to pick up the newer version.');
                    window.location.reload();
                    return;
                }

                if (!response.ok) {
                    throw new Error(`Save failed with status ${response.status}`);
                }

                // debugLog('Save completed, reloading page');
                // Save completed successfully, now reload
                window.location.reload();
            } else if (forceSave) {
                // Fallback: use forceSave
                await forceSave();
                // debugLog('Save completed, reloading page');
                window.location.reload();
            }
        } catch (error) {
            debugError('Failed to save before refresh:', error);
            // Even if save fails, allow refresh to proceed
            window.location.reload();
        } finally {
            isHandlingRefreshRef.current = false;
        }
    }, [currentCase, formData, forceSave]);

    const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
        // debugLog('handleBeforeUnload called', { isWizardPage, hasUnsavedChanges, isHandlingRefresh: isHandlingRefreshRef.current });

        if (!isWizardPage) return;

        // If we're already handling a refresh (from keyboard shortcut), don't show dialog
        // The keyboard handler will wait for save to complete before reloading
        if (isHandlingRefreshRef.current) {
            return;
        }

        if (!hasUnsavedChanges) return;

        // Manual Save mode: never auto-save on unload. Show the browser's native
        // "unsaved changes" dialog and let the user decide — if they force-close anyway,
        // those edits are lost by design (this is the user's explicit discard path).
        if (manualMode) {
            event.preventDefault();
            event.returnValue = '';
            return '';
        }

        if (forceSave) {
            // debugLog('Auto-saving before page refresh/unload - preventing refresh until save completes');
            // Prevent the refresh from happening immediately so we can save first
            event.preventDefault();
            // Modern browsers require returnValue to be set to show a dialog
            // But we want to minimize the dialog, so we set it to empty string
            // Some browsers may still show a minimal dialog, but we can't control that
            event.returnValue = '';

            // Start the save operation and reload once it completes
            // Note: We can't await in beforeunload, but we can start the async operation
            // and it will reload when done
            performSaveAndReload();

            // Return empty string - this should minimize the dialog in most browsers
            return '';
        }
    }, [isWizardPage, hasUnsavedChanges, forceSave, manualMode, performSaveAndReload]);

    // Handle browser back button
    const handlePopState = useCallback(async (event: PopStateEvent) => {
        if (!isWizardPage || isHandlingBackButtonRef.current) {
            return;
        }

        if (hasUnsavedChanges) {
            // debugLog('Browser back button detected');
            // Store the path we were on before the back button (the current location after popstate)
            // The popstate event fires AFTER navigation, so we need to go back to where we were
            const currentPathAfterBack = window.location.pathname;
            // We were on the previous location before the back button
            const pathWeWereOn = previousLocationRef.current;

            // Prevent the navigation by pushing the current state back to where we were
            window.history.pushState(null, '', pathWeWereOn);

            // Store the path we want to navigate to after saving (the page we just came from)
            previousPathBeforeBackRef.current = currentPathAfterBack;

            // Manual Save mode: never auto-save. Prompt via the same modal; on Save/Discard
            // the existing handleSave/handleDiscard consume pendingNavigation and navigate.
            if (manualMode) {
                setPendingNavigation(currentPathAfterBack);
                setShowSaveModal(true);
                previousPathBeforeBackRef.current = null;
                return;
            }

            if (!forceSave) return;

            isHandlingBackButtonRef.current = true;
            setIsSaving(true);

            try {
                await forceSave();
                if (releaseCaseEditingLock) {
                    try {
                        await releaseCaseEditingLock();
                    } catch (err) {
                        debugError('useNavigationGuard: releaseCaseEditingLock failed (back button)', err);
                    }
                }
                // debugLog('Saved successfully before back navigation');
                // Navigate to the page we were trying to go back to
                // Since we pushed a state when intercepting, the history stack is:
                // [previous page, current page, current page (pushed)]
                // We need to go back 2 steps to get to the previous page
                window.history.go(-2);
                previousPathBeforeBackRef.current = null;
            } catch (error) {
                debugError('Save before back navigation failed:', error);
                // Show modal as fallback
                setPendingNavigation(previousPathBeforeBackRef.current);
                setShowSaveModal(true);
            } finally {
                setIsSaving(false);
                isHandlingBackButtonRef.current = false;
            }
        }
    }, [isWizardPage, hasUnsavedChanges, forceSave, manualMode, navigate, releaseCaseEditingLock]);

    // Set up history state for back button interception
    useEffect(() => {
        if (isWizardPage && hasUnsavedChanges && previousLocationRef.current === location.pathname) {
            // Only push state once per location when there are unsaved changes
            // This allows us to intercept the back button
            window.history.pushState({ preventBack: true }, '', location.pathname);
        }
        previousLocationRef.current = location.pathname;
    }, [isWizardPage, hasUnsavedChanges, location.pathname]);

    // Handle keyboard shortcuts for refresh (F5, Ctrl+R, Ctrl+Shift+R)
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        // Manual Save mode: don't intercept refresh shortcuts — let them fall through to
        // the native beforeunload dialog (handleBeforeUnload already shows it, no auto-save).
        if (!isWizardPage || !hasUnsavedChanges || !forceSave || manualMode) return;

        // Check for refresh shortcuts
        const isRefreshKey =
            event.key === 'F5' ||
            (event.key === 'r' && (event.ctrlKey || event.metaKey) && !event.shiftKey) ||
            (event.key === 'R' && (event.ctrlKey || event.metaKey) && event.shiftKey);

        if (isRefreshKey) {
            event.preventDefault();
            event.stopPropagation();

            // debugLog('Refresh shortcut detected, saving before refresh');
            // Use the shared save and reload function
            performSaveAndReload();
        }
    }, [isWizardPage, hasUnsavedChanges, forceSave, manualMode, performSaveAndReload]);

    // Handle visibility change to save before page becomes hidden (refresh, tab close, etc.)
    const handleVisibilityChange = useCallback(() => {
        // Manual Save mode: no keepalive save on hide — the native beforeunload dialog
        // (hard close) or the in-app prompt (tab switch back to a nav action) covers it.
        if (manualMode) return;
        if (document.hidden && isWizardPage && hasUnsavedChanges && forceSave && !isHandlingRefreshRef.current) {
            // debugLog('Page becoming hidden, saving before refresh/close');
            // Page is about to be hidden (refresh, tab close, etc.)
            // Start save operation - it will use keepalive to continue after page unloads
            if (currentCase?.case?.basic?.caseId && (formData || currentCase.case)) {
                const caseData = formData || currentCase.case;

                // Normalize stepDowns if needed
                const normalizedCaseData = { ...caseData };
                if (normalizedCaseData.parties?.partyA?.tax?.stepDowns) {
                    const stepDownsA = normalizedCaseData.parties.partyA.tax.stepDowns;
                    if (!Array.isArray(stepDownsA) && typeof stepDownsA === 'object') {
                        const keys = Object.keys(stepDownsA).map(k => parseInt(k)).filter(k => !isNaN(k)).sort((a, b) => a - b);
                        normalizedCaseData.parties.partyA.tax.stepDowns = keys.map(k => (stepDownsA as any)[k.toString()]);
                    }
                }
                if (normalizedCaseData.parties?.partyB?.tax?.stepDowns) {
                    const stepDownsB = normalizedCaseData.parties.partyB.tax.stepDowns;
                    if (!Array.isArray(stepDownsB) && typeof stepDownsB === 'object') {
                        const keys = Object.keys(stepDownsB).map(k => parseInt(k)).filter(k => !isNaN(k)).sort((a, b) => a - b);
                        normalizedCaseData.parties.partyB.tax.stepDowns = keys.map(k => (stepDownsB as any)[k.toString()]);
                    }
                }

                // Use keepalive to ensure request continues after page unloads
                // Use the no-merge endpoint to preserve the master-copy invariant — server must
                // not auto-integrate client-submitted expenses on a tab-close path.
                const url = `${config.apiBaseUrl}/v1/cases/save-no-merge`;
                // Same conditional write as the reload path. Nothing here can react to a 412 --
                // the page is going away -- which is exactly why the header has to be sent: the
                // server rejecting a stale overwrite is the only thing protecting newer work.
                const etag = currentCase.etag || '';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(etag ? { 'If-Match': etag } : {}),
                    },
                    body: JSON.stringify(normalizedCaseData),
                    credentials: 'include',
                    keepalive: true,
                }).catch(err => {
                    debugError('Failed to save on visibility change:', err);
                });
            }
        }
    }, [isWizardPage, hasUnsavedChanges, forceSave, currentCase, formData, manualMode]);

    // pagehide fires more reliably than visibilitychange on mobile and in Safari
    // when the tab is closed or navigated away from (R6). Use BackgroundSaveService's
    // keepalive beacon so the request survives hard tab kills.
    // Manual Save mode: skip entirely — no keepalive save on tab close/navigate-away.
    const handlePageHide = useCallback(() => {
        if (!isWizardPage || !hasUnsavedChanges || manualMode) return;
        BackgroundSaveService.flushBeacon(config.apiBaseUrl);
    }, [isWizardPage, hasUnsavedChanges, manualMode]);

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload, { capture: true });
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);

        // Listen for popstate (browser back/forward button)
        if (isWizardPage) {
            window.addEventListener('popstate', handlePopState);
        }

        // Listen for keyboard shortcuts for refresh
        if (isWizardPage && hasUnsavedChanges) {
            window.addEventListener('keydown', handleKeyDown, true);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload, { capture: true });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [handleBeforeUnload, handleVisibilityChange, handlePageHide, handlePopState, handleKeyDown, isWizardPage, hasUnsavedChanges]);

    return {
        showSaveModal,
        isSaving,
        handleSave,
        handleDiscard,
        handleCancel,
        checkNavigation,
        checkAction
    };
};
